import { supabase } from '@/lib/supabase/client';
import { normalizeProofLinkWriteError } from '@/lib/customer-ops/proof-link-errors';
import type {
  ContentQueueInsert,
  ContentQueueItem,
  ContentQueueListFilters,
  ContentQueueStatus,
  ContentQueueUpdate,
} from '@/types/customer-ops';

function requiredText(value: string, fieldName: string): string {
  const trimmed = value.trim();
  if (!trimmed) {
    throw new Error(`${fieldName} is required.`);
  }
  return trimmed;
}

function nullableText(value: string | null | undefined): string | null {
  if (value === null || value === undefined) return null;
  const trimmed = value.trim();
  return trimmed || null;
}

function validateStatusFields(
  status: ContentQueueStatus,
  fields: {
    scheduled_for?: string | null;
    published_url?: string | null;
    killed_reason?: string | null;
  }
): void {
  if (status === 'scheduled' && !fields.scheduled_for) {
    throw new Error('Scheduled date/time is required for scheduled posts.');
  }
  if (status === 'published' && !nullableText(fields.published_url)) {
    throw new Error('Published URL is required for published posts.');
  }
  if (status === 'killed' && !nullableText(fields.killed_reason)) {
    throw new Error('Killed reason is required when marking a post killed.');
  }
}

type ContentQueueFilterQuery<T> = {
  eq(column: string, value: string): T;
  in(column: string, values: readonly string[]): T;
};

function applyContentQueueFilters<T extends ContentQueueFilterQuery<T>>(
  query: T,
  filters?: ContentQueueListFilters
): T {
  let next = query;
  if (!filters) return next;

  if (filters.status) {
    const statuses = Array.isArray(filters.status) ? filters.status : [filters.status];
    next = next.in('status', statuses);
  }
  if (filters.contentType) {
    const contentTypes = Array.isArray(filters.contentType) ? filters.contentType : [filters.contentType];
    next = next.in('content_type', contentTypes);
  }
  if (filters.channel) {
    const channels = Array.isArray(filters.channel) ? filters.channel : [filters.channel];
    next = next.in('channel', channels);
  }
  if (filters.locationId) {
    next = next.eq('location_id', filters.locationId);
  }
  return next;
}

export const contentQueueService = {
  async list(filters?: ContentQueueListFilters): Promise<ContentQueueItem[]> {
    try {
      let query = supabase
        .from('content_queue')
        .select('*')
        .order('updated_at', { ascending: false });
      query = applyContentQueueFilters(query, filters);

      const { data, error } = await query;
      if (error) {
        console.error('Error fetching content queue rows:', error);
        return [];
      }
      return (data as ContentQueueItem[]) || [];
    } catch (error) {
      console.error('Error fetching content queue rows:', error);
      return [];
    }
  },

  async getById(id: string): Promise<ContentQueueItem | null> {
    try {
      const { data, error } = await supabase.from('content_queue').select('*').eq('id', id).single();
      if (error) {
        if (error.code === 'PGRST116') return null;
        console.error('Error fetching content queue row:', error);
        return null;
      }
      return (data as ContentQueueItem) || null;
    } catch (error) {
      console.error('Error fetching content queue row:', error);
      return null;
    }
  },

  async create(data: ContentQueueInsert): Promise<ContentQueueItem> {
    const status = data.status ?? 'draft';
    validateStatusFields(status, {
      scheduled_for: data.scheduled_for,
      published_url: data.published_url,
      killed_reason: data.killed_reason,
    });

    const insertData = {
      title: requiredText(data.title, 'Title'),
      content_type: data.content_type,
      channel: data.channel,
      status,
      draft_text: data.draft_text ?? '',
      approver: nullableText(data.approver),
      scheduled_for: data.scheduled_for ?? null,
      published_url: nullableText(data.published_url),
      killed_reason: nullableText(data.killed_reason),
      proof_metric_ids: data.proof_metric_ids ?? [],
      location_id: data.location_id ?? null,
      jurisdiction_tag: data.jurisdiction_tag?.trim() || 'ALL-US',
      trigger_source: data.trigger_source ?? 'manual',
      notes: nullableText(data.notes),
    };

    const { data: created, error } = await supabase
      .from('content_queue')
      .insert(insertData)
      .select()
      .single();

    if (error) {
      const proofLinkError = normalizeProofLinkWriteError(error);
      if (proofLinkError) {
        console.error('Content queue proof-link write rejected:', proofLinkError.code);
        throw proofLinkError;
      }
      console.error('Error creating content queue row:', error);
      throw new Error(`Failed to create content queue row: ${error.message}`);
    }
    return created as ContentQueueItem;
  },

  async update(id: string, data: ContentQueueUpdate): Promise<ContentQueueItem> {
    const updateData: Record<string, unknown> = {};
    if (data.title !== undefined) updateData.title = requiredText(data.title, 'Title');
    if (data.content_type !== undefined) updateData.content_type = data.content_type;
    if (data.channel !== undefined) updateData.channel = data.channel;
    if (data.status !== undefined) updateData.status = data.status;
    if (data.draft_text !== undefined) updateData.draft_text = data.draft_text;
    if (data.approver !== undefined) updateData.approver = nullableText(data.approver);
    if (data.scheduled_for !== undefined) updateData.scheduled_for = data.scheduled_for;
    if (data.published_url !== undefined) updateData.published_url = nullableText(data.published_url);
    if (data.killed_reason !== undefined) updateData.killed_reason = nullableText(data.killed_reason);
    if (data.proof_metric_ids !== undefined) updateData.proof_metric_ids = data.proof_metric_ids;
    if (data.location_id !== undefined) updateData.location_id = data.location_id;
    if (data.jurisdiction_tag !== undefined) {
      updateData.jurisdiction_tag = data.jurisdiction_tag.trim() || 'ALL-US';
    }
    if (data.trigger_source !== undefined) updateData.trigger_source = data.trigger_source;
    if (data.notes !== undefined) updateData.notes = nullableText(data.notes);

    if (data.status !== undefined) {
      validateStatusFields(data.status, {
        scheduled_for:
          data.scheduled_for !== undefined ? data.scheduled_for : (updateData.scheduled_for as string | null),
        published_url:
          data.published_url !== undefined
            ? nullableText(data.published_url)
            : (updateData.published_url as string | null),
        killed_reason:
          data.killed_reason !== undefined
            ? nullableText(data.killed_reason)
            : (updateData.killed_reason as string | null),
      });
    }

    const { data: updated, error } = await supabase
      .from('content_queue')
      .update(updateData)
      .eq('id', id)
      .select()
      .single();

    if (error) {
      const proofLinkError = normalizeProofLinkWriteError(error);
      if (proofLinkError) {
        console.error('Content queue proof-link write rejected:', proofLinkError.code);
        throw proofLinkError;
      }
      console.error('Error updating content queue row:', error);
      throw new Error(`Failed to update content queue row: ${error.message}`);
    }
    return updated as ContentQueueItem;
  },

  async setStatus(
    id: string,
    status: ContentQueueStatus,
    fields?: {
      approver?: string | null;
      scheduled_for?: string | null;
      published_url?: string | null;
      killed_reason?: string | null;
    }
  ): Promise<ContentQueueItem> {
    return this.update(id, {
      status,
      approver: fields?.approver,
      scheduled_for: fields?.scheduled_for,
      published_url: fields?.published_url,
      killed_reason: fields?.killed_reason,
    });
  },

  async countByStatus(statuses: ContentQueueStatus[]): Promise<number> {
    const rows = await this.list({ status: statuses });
    return rows.length;
  },
};
